import React from "react";

import type { StrategyRdHeadlineStats, StrategyRdHoldoutSplit } from "../../../types/strategyRd";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import { StrategyRdBadge, StrategyRdInfo, type StrategyRdTone } from "./strategyRdLabUi";

/** "Apr 13" / "Apr 13, 2025" (the year only when it isn't this year). */
export function strategyRdShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }), timeZone: "UTC" });
}

function range(a: string | null | undefined, b: string | null | undefined): string | null {
  const x = strategyRdShortDate(a);
  const y = strategyRdShortDate(b);
  return x && y ? `${x} – ${y}` : null;
}

export const HOLDOUT_VERDICTS: Record<string, { label: string; color: string; tip: string }> = {
  held_up: {
    label: "Held up",
    color: "#16a34a",
    tip: "The unseen period kept a positive expectancy, at least half the tuning period's.",
  },
  weaker: {
    label: "Weaker",
    color: "#64748B",
    tip: "Still positive on the unseen period, but under half the tuning period's expectancy.",
  },
  failed: { label: "Failed", color: "#dc2626", tip: "The unseen period lost money (expectancy ≤ 0)." },
  too_few_trades: {
    label: "Too few trades",
    color: "#6b7280",
    tip: "Fewer than 5 trades in the unseen period. Not enough to judge.",
  },
};

export type StrategyRdVerdictTone = { label: string; color: string; tip: string; tone?: StrategyRdTone };

const TONE_BY_COLOR: Record<string, StrategyRdTone> = {
  "#16a34a": "good",
  "#64748B": "warn",
  "#dc2626": "bad",
};

export function StrategyRdVerdictChip({
  verdict,
  map,
  attr,
}: {
  verdict: string | null | undefined;
  map: Record<string, StrategyRdVerdictTone>;
  attr?: string;
}): JSX.Element | null {
  if (!verdict) return null;
  const v = map[verdict] ?? { label: verdict.replace(/_/g, " "), color: "#6b7280", tip: "" };
  return (
    <StrategyRdBadge
      tone={v.tone ?? TONE_BY_COLOR[v.color.toLowerCase()] ?? "neutral"}
      title={v.tip || undefined}
      data-strategy-rd-verdict={verdict}
      {...(attr ? { [attr]: verdict } : {})}
    >
      {v.label}
    </StrategyRdBadge>
  );
}

export function StrategyRdHoldoutVerdictChip({ verdict }: { verdict: string | null | undefined }): JSX.Element | null {
  return <StrategyRdVerdictChip verdict={verdict} map={HOLDOUT_VERDICTS} attr="data-strategy-rd-holdout-verdict" />;
}

function fmtR(v: number | null | undefined): string {
  return v == null ? "N/A" : `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(2)}R`;
}

/** "18 trades · 0.42R / trade". */
export function strategyRdHeadlineBrief(stats: StrategyRdHeadlineStats | null | undefined): string {
  if (!stats) return "N/A";
  const n = stats.closed_count;
  const parts = [
    n != null ? `${n} trade${n === 1 ? "" : "s"}` : null,
    stats.expectancy_r != null ? `${fmtR(stats.expectancy_r)} / trade` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "N/A";
}

/** Hold-out: "Tuning period … / Unseen period …" with the verdict chip. */
export function StrategyRdHoldoutRow({
  split,
  compact = false,
}: {
  split: StrategyRdHoldoutSplit | null | undefined;
  compact?: boolean;
}): JSX.Element | null {
  if (!split) return null;
  const pct = split.holdout_pct != null ? Math.round(split.holdout_pct) : null;
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${compact ? "text-[11px]" : "text-xs"}`}
      data-strategy-rd-holdout=""
      title={
        pct != null
          ? `The last ${pct}% of the window was held out: a change that only works in the tuning period shows up here.`
          : undefined
      }
    >
      <span className={strategyRdMetaClassName}>
        Tuning period{range(split.first_time, split.cut_time) ? ` (${range(split.first_time, split.cut_time)})` : ""}{" "}
        <span className="text-gray-500 dark:text-white">{strategyRdHeadlineBrief(split.tuning)}</span>
      </span>
      <span className={strategyRdMetaClassName}>
        Unseen period
        {range(split.cut_time, split.last_time)
          ? ` (${range(split.cut_time, split.last_time)}${pct != null ? `, last ${pct}%` : ""})`
          : pct != null
            ? ` (last ${pct}%)`
            : ""}{" "}
        <span className="text-gray-500 dark:text-white">{strategyRdHeadlineBrief(split.unseen)}</span>
      </span>
      <StrategyRdHoldoutVerdictChip verdict={split.verdict} />
      <StrategyRdInfo title="Tuning vs unseen period">
        <p>
          The backtest is cut in two by time. You (and TES) tweak the strategy while looking at the <strong>tuning
          period</strong>; the last {pct ?? 30}%. The <strong>unseen period</strong>. Is kept aside as a fresh test.
        </p>
        <p>
          A real edge keeps working on data it wasn't tuned on. If the unseen period loses money, the strategy was
          probably fitted to the past (overfitting) rather than finding something that repeats.
        </p>
        <p>
          <strong>Held up</strong>: still at least half as good. <strong>Weaker</strong>: positive, but less than half.{" "}
          <strong>Failed</strong>: lost money. <strong>Too few trades</strong>: under 5 trades to judge.
        </p>
      </StrategyRdInfo>
    </div>
  );
}
